import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getMessagesLogReport,
  MessageLog,
  downloadMessagesLogReportPDF,
  downloadMessagesLogReportExcel,
} from '../../services/api';
import { getMaxDateToday, getMaxEndDateForStart } from '../../utils/dateUtils';

type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const MessageLogsReport: React.FC = () => {
  const [rows, setRows] = useState<MessageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'true' | 'false'>('');
  const [channelFilter, setChannelFilter] = useState<'' | 'whatsapp' | 'sms'>('');
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [listMeta, setListMeta] = useState<{
    total: number;
    returned: number;
    limit: number;
    truncated: boolean;
  } | null>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTheme = () => setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const getDateRange = (): { start?: number; end?: number } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilterType) {
      case 'today': {
        const start = new Date(today);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'yesterday': {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) return {};
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      default:
        return {};
    }
  };

  const fetchData = async () => {
    const dateRange = getDateRange();
    setLoading(true);
    try {
      const res = await getMessagesLogReport(dateRange.start, dateRange.end, statusFilter, channelFilter, searchTerm || undefined);
      setRows(res.data.data || []);
      setListMeta(res.data.meta ?? null);
    } catch (error) {
      console.error('Failed to load messages log report', error);
      setRows([]);
      setListMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, channelFilter, dateFilterType, customStartDate, customEndDate]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((item) =>
      item.metadata.name?.toLowerCase().includes(q) ||
      item.spec.receiverNumber?.toLowerCase().includes(q) ||
      item.spec.receiverName?.toLowerCase().includes(q) ||
      item.spec.bookingId?.toLowerCase().includes(q) ||
      item.spec.messageText?.toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  const formatDateTime = (ts?: number) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);
      const dateRange = getDateRange();

      const response = format === 'pdf'
        ? await downloadMessagesLogReportPDF(dateRange.start, dateRange.end, statusFilter, channelFilter, searchTerm || undefined)
        : await downloadMessagesLogReportExcel(dateRange.start, dateRange.end, statusFilter, channelFilter, searchTerm || undefined);

      const { url } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      link.download = `messages-log-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <div><div className="loading">Loading message logs...</div></div>;
  }

  return (
    <div>
      {listMeta?.truncated && (
        <div
          role="status"
          style={{
            marginBottom: '12px',
            padding: '10px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: 1.45,
            background: isDarkTheme ? 'rgba(251, 191, 36, 0.12)' : '#fff7ed',
            border: `1px solid ${isDarkTheme ? 'rgba(251, 191, 36, 0.35)' : '#fed7aa'}`,
            color: isDarkTheme ? '#fde68a' : '#9a3412',
          }}
        >
          Showing the newest <strong>{listMeta.returned.toLocaleString()}</strong> of{' '}
          <strong>{listMeta.total.toLocaleString()}</strong> messages in this range (server limit{' '}
          {listMeta.limit.toLocaleString()}). Narrow the date range, use filters, export to Excel/PDF, or ask ops to
          raise <code style={{ fontSize: '12px' }}>MESSAGES_LOG_REPORT_MAX</code> (max 100,000).
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: downloadError ? '10px' : '20px', flexWrap: 'wrap', gap: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          <input
            type="text"
            placeholder="Search number, name, bookingId, message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ minWidth: '220px', width: '220px', flex: '0 0 220px' }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | 'true' | 'false')}
            className="reports-filter-select"
            style={{ minWidth: '140px', width: '140px', flex: '0 0 140px' }}
          >
            <option value="">All Status</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value as '' | 'whatsapp' | 'sms')}
            className="reports-filter-select"
            style={{ minWidth: '140px', width: '140px', flex: '0 0 140px' }}
          >
            <option value="">All Channel</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
          <select
            value={dateFilterType}
            onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
            className="reports-filter-select"
            style={{ minWidth: '150px', width: '150px', flex: '0 0 150px' }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Dates</option>
          </select>

          <div style={{ position: 'relative' }} ref={downloadMenuRef}>
          <button
            onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            disabled={downloading || filteredRows.length === 0}
            style={{
              padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none',
              borderRadius: '6px', cursor: downloading || filteredRows.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '16px', fontWeight: '500', opacity: downloading || filteredRows.length === 0 ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            {downloading ? 'Generating...' : 'Download'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          {showDownloadMenu && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: '5px',
              backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
              border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
              borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: '150px',
            }}>
              <button
                onClick={() => handleDownload('pdf')}
                disabled={downloading}
                style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#fff' : '#333', border: 'none', borderRadius: '6px 6px 0 0', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Download as PDF
              </button>
              <button
                onClick={() => handleDownload('excel')}
                disabled={downloading}
                style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#fff' : '#333', border: 'none', borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd', borderRadius: '0 0 6px 6px', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Download as Excel
              </button>
            </div>
          )}
          </div>
        </div>

      </div>
      {dateFilterType === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
          <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>Start Date:</label>
          <input
            type="date"
            value={customStartDate}
            onChange={(e) => {
              const value = e.target.value;
              setCustomStartDate(value);
              if (value && customEndDate) {
                const maxEnd = getMaxEndDateForStart(value);
                if (customEndDate > maxEnd) setCustomEndDate(maxEnd);
              }
            }}
            max={getMaxDateToday()}
            className="reports-date-input"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
          />
          <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>End Date:</label>
          <input
            type="date"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            min={customStartDate || undefined}
            max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()}
            className="reports-date-input"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
          />
        </div>
      )}
      {downloadError && (
        <div style={{ padding: '10px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33', fontSize: '16px', marginBottom: '20px' }}>
          {downloadError}
        </div>
      )}

      {filteredRows.length === 0 ? (
        <div className="empty-state">
          <h3>No message logs found</h3>
          <p>Try changing filters or date range.</p>
        </div>
      ) : (
        <div
          className="table-container"
          style={{
            height: 'calc(100vh - 360px)',
            overflowY: 'auto',
            overflowX: 'auto',
            border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
            backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
          }}
        >
          <table className="bookings-table" style={{ minWidth: '1200px' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Channel</th>
                <th>Receiver Name</th>
                <th>Receiver Number</th>
                <th>Booking Reference</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((item) => (
                <tr key={item._id}>
                  <td>{formatDateTime(item.spec.sentAt || item.metadata.createdAt)}</td>
                  <td>{(item.spec.channel || 'whatsapp').toUpperCase()}</td>
                  <td>{item.receiverInfo?.name || item.spec.receiverName || '—'}</td>
                  <td>{item.receiverInfo?.phone || item.spec.receiverNumber || '—'}</td>
                  <td>{item.bookingInfo?.reference || '—'}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 8px',
                      borderRadius: '5px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: item.spec.success ? '#16a34a' : '#dc2626',
                      backgroundColor: item.spec.success ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
                    }}>
                      {item.spec.success ? 'SUCCESS' : 'FAILURE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MessageLogsReport;
